/**
 * Compute splat ground-plane north bearing from >= 2 GCPs (least-squares 2D rotation).
 * Stable path: orthonormal (u, v) on the ground plane matches
 * rotationPcToEnuFromPlaneAndNorth in mapAlignmentSolver.js at beta = 0 (E0, N0).
 */

export type GroundPlaneInput = {
    normal: [number, number, number];
    pointOnPlane: [number, number, number];
};

export type GcpForBearing = {
    splat: [number, number, number];
    lat: number;
    lng: number;
};

const RAD_TO_DEG = 180 / Math.PI;
const R_EARTH = 6371000;

function mod360(d: number): number {
    return ((d % 360) + 360) % 360;
}

function len3(x: number, y: number, z: number): number {
    return Math.hypot(x, y, z);
}

function normalize3(nx: number, ny: number, nz: number): [number, number, number] {
    const l = len3(nx, ny, nz) || 1;
    return [nx / l, ny / l, nz / l];
}

/** Cross product a x b */
function cross(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number
): [number, number, number] {
    return [
        ay * bz - az * by,
        az * bx - ax * bz,
        ax * by - ay * bx
    ];
}

function dot3(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
    return ax * bx + ay * by + az * bz;
}

/**
 * Ground-plane basis (u, v) = (E0, N0) from mapAlignmentSolver at beta = 0:
 * same as ‖(U × ref)‖ → N0 = ‖(E_temp × U)‖ → E0 = ‖(N0 × U)‖.
 */
function planeBasis(nx: number, ny: number, nz: number): {
    ux: number; uy: number; uz: number;
    vx: number; vy: number; vz: number;
} {
    const U = normalize3(nx, ny, nz);
    const [ux0, uy0, uz0] = U;
    const ref: [number, number, number] =
        Math.abs(uy0) > 0.85 ? [0, 0, 1] : [0, 1, 0];
    let [etx, ety, etz] = cross(ux0, uy0, uz0, ref[0], ref[1], ref[2]);
    let el = len3(etx, ety, etz);
    if (el < 1e-6) {
        etx = 1;
        ety = 0;
        etz = 0;
    } else {
        etx /= el;
        ety /= el;
        etz /= el;
    }
    let [n0x, n0y, n0z] = cross(etx, ety, etz, ux0, uy0, uz0);
    let nl = len3(n0x, n0y, n0z);
    if (nl < 1e-6) {
        n0x = 0;
        n0y = 0;
        n0z = 1;
    } else {
        n0x /= nl;
        n0y /= nl;
        n0z /= nl;
    }
    const [ux, uy, uz] = normalize3(
        n0y * uz0 - n0z * uy0,
        n0z * ux0 - n0x * uz0,
        n0x * uy0 - n0y * ux0,
    );
    return { ux, uy, uz, vx: n0x, vy: n0y, vz: n0z };
}

/** Project point onto plane through p0 with normal n; returns vector from p0 along plane only */
function projectOnPlane(
    wx: number, wy: number, wz: number,
    p0x: number, p0y: number, p0z: number,
    nx: number, ny: number, nz: number
): [number, number, number] {
    const dx = wx - p0x;
    const dy = wy - p0y;
    const dz = wz - p0z;
    const nn = dot3(dx, dy, dz, nx, ny, nz);
    return [dx - nx * nn, dy - ny * nn, dz - nz * nn];
}

/**
 * 2D Kabsch rotation angle (radians): maps centered local (pu, pv) to centered (E, N).
 */
function kabsch2dRotationRad(
    pu: number[],
    pv: number[],
    e: number[],
    nCoord: number[]
): number {
    let s00 = 0;
    let s01 = 0;
    let s10 = 0;
    let s11 = 0;
    const m = pu.length;
    for (let i = 0; i < m; i++) {
        s00 += pu[i] * e[i];
        s01 += pu[i] * nCoord[i];
        s10 += pv[i] * e[i];
        s11 += pv[i] * nCoord[i];
    }
    return Math.atan2(s01 - s10, s00 + s11);
}

export function solveBearingFromGcps(
    groundPlane: GroundPlaneInput,
    gcps: GcpForBearing[]
): { bearingDeg: number; residualDeg: number; basisNorth: [number, number, number]; basisUp: [number, number, number] } | null {
    if (gcps.length < 2) return null;

    const [nx, ny, nz] = normalize3(
        groundPlane.normal[0],
        groundPlane.normal[1],
        groundPlane.normal[2]
    );
    const p0x = groundPlane.pointOnPlane[0];
    const p0y = groundPlane.pointOnPlane[1];
    const p0z = groundPlane.pointOnPlane[2];

    const { ux, uy, uz, vx, vy, vz } = planeBasis(nx, ny, nz);

    const lat0 = gcps[0].lat;
    const lng0 = gcps[0].lng;

    const pu: number[] = [];
    const pv: number[] = [];
    const e: number[] = [];
    const nArr: number[] = [];

    for (let i = 0; i < gcps.length; i++) {
        const g = gcps[i];
        const [px, py, pz] = projectOnPlane(g.splat[0], g.splat[1], g.splat[2], p0x, p0y, p0z, nx, ny, nz);
        const uu = dot3(px, py, pz, ux, uy, uz);
        const vv = dot3(px, py, pz, vx, vy, vz);
        const dLat = ((g.lat - lat0) * Math.PI) / 180;
        const dLng = ((g.lng - lng0) * Math.PI) / 180;
        const dE = R_EARTH * Math.cos((lat0 * Math.PI) / 180) * dLng;
        const dN = R_EARTH * dLat;
        pu.push(uu);
        pv.push(vv);
        e.push(dE);
        nArr.push(dN);
    }

    const n = gcps.length;
    let meanU = 0;
    let meanV = 0;
    let meanE = 0;
    let meanN = 0;
    for (let i = 0; i < n; i++) {
        meanU += pu[i];
        meanV += pv[i];
        meanE += e[i];
        meanN += nArr[i];
    }
    meanU /= n;
    meanV /= n;
    meanE /= n;
    meanN /= n;

    for (let i = 0; i < n; i++) {
        pu[i] -= meanU;
        pv[i] -= meanV;
        e[i] -= meanE;
        nArr[i] -= meanN;
    }

    const theta = kabsch2dRotationRad(pu, pv, e, nArr);
    const bearingDeg = mod360(theta * RAD_TO_DEG);
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    const basisNorth = normalize3(
        s * ux + c * vx,
        s * uy + c * vy,
        s * uz + c * vz
    );

    let sumSq = 0;
    let sumNorm = 0;
    for (let i = 0; i < n; i++) {
        const predE = c * pu[i] - s * pv[i];
        const predN = s * pu[i] + c * pv[i];
        const de = predE - e[i];
        const dn = predN - nArr[i];
        sumSq += de * de + dn * dn;
        sumNorm += Math.hypot(e[i], nArr[i]) + Math.hypot(pu[i], pv[i]);
    }
    const rmse = Math.sqrt(sumSq / n);
    const meanRadius = sumNorm / (2 * n) || 1;
    const residualDeg = Math.min(90, (rmse / meanRadius) * RAD_TO_DEG);

    return { bearingDeg, residualDeg, basisNorth, basisUp: [nx, ny, nz] };
}
